import React, { useState } from 'react';
import { X, Save, Info } from 'lucide-react';
import { updateMultiplicateurs } from '../services/api';

function MultiplicateurModal({ challenge, onClose, onUpdate }) {
  const [multiplicateurs, setMultiplicateurs] = useState(
    challenge.participants.reduce((acc, p) => {
      acc[p._id] = p.multiplicateur || 1.0;
      return acc;
    }, {})
  );
  const [saving, setSaving] = useState(false);

  const handleChange = (participantId, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0.1 || numValue > 5.0) return;
    
    setMultiplicateurs({
      ...multiplicateurs,
      [participantId]: numValue
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMultiplicateurs(challenge._id, multiplicateurs);
      alert('✅ Multiplicateurs mis à jour !');
      onUpdate();
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la mise à jour');
      setSaving(false);
    }
  };

  const getPreset = (level) => {
    const presets = {
      'elite': 0.8,
      'avance': 1.0,
      'intermediaire': 1.5,
      'debutant': 2.0,
      'grand-debutant': 2.5
    };
    return presets[level];
  };

  const applyPreset = (participantId, level) => {
    setMultiplicateurs({
      ...multiplicateurs,
      [participantId]: getPreset(level)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Gérer les multiplicateurs</h2>
              <p className="text-indigo-100 text-sm mt-1">
                Ajustez les handicaps pour rendre le challenge équitable
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="p-4 bg-blue-50 border-b">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Comment ça marche ?</p>
              <p>
                Un multiplicateur de <strong>x2.0</strong> signifie que 10 km parcourus comptent pour <strong>20 km</strong> dans le classement.
                Plus le multiplicateur est élevé, plus l'avantage est important.
              </p>
            </div>
          </div>
        </div>

        {/* Liste des participants */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {challenge.participants.map((participant) => (
              <div
                key={participant._id}
                className="border-2 border-gray-200 rounded-xl p-4 hover:border-indigo-300 transition"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                      {participant.user.prenom?.[0]}{participant.user.nom?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {participant.user.prenom} {participant.user.nom}
                      </p>
                      <p className="text-sm text-gray-600">
                        {participant.totalDistance.toFixed(1)} km réels
                        {multiplicateurs[participant._id] !== 1.0 && (
                          <span className="text-indigo-600 font-semibold ml-2">
                            → {(participant.totalDistance * multiplicateurs[participant._id]).toFixed(1)} km (classement)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Slider multiplicateur */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Multiplicateur: <span className="text-indigo-600 font-bold">x{multiplicateurs[participant._id]}</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="5.0"
                      value={multiplicateurs[participant._id]}
                      onChange={(e) => handleChange(participant._id, e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-center font-semibold"
                    />
                  </div>

                  <input
                    type="range"
                    min="0.1"
                    max="5.0"
                    step="0.1"
                    value={multiplicateurs[participant._id]}
                    onChange={(e) => handleChange(participant._id, e.target.value)}
                    className="w-full"
                  />

                  {/* Presets rapides */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => applyPreset(participant._id, 'elite')}
                      className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold hover:bg-purple-200 transition"
                    >
                      Élite (x0.8)
                    </button>
                    <button
                      onClick={() => applyPreset(participant._id, 'avance')}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold hover:bg-blue-200 transition"
                    >
                      Avancé (x1.0)
                    </button>
                    <button
                      onClick={() => applyPreset(participant._id, 'intermediaire')}
                      className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold hover:bg-green-200 transition"
                    >
                      Intermédiaire (x1.5)
                    </button>
                    <button
                      onClick={() => applyPreset(participant._id, 'debutant')}
                      className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-semibold hover:bg-orange-200 transition"
                    >
                      Débutant (x2.0)
                    </button>
                    <button
                      onClick={() => applyPreset(participant._id, 'grand-debutant')}
                      className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold hover:bg-red-200 transition"
                    >
                      Grand débutant (x2.5)
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50"
          >
            {saving ? (
              'Enregistrement...'
            ) : (
              <>
                <Save size={20} />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MultiplicateurModal;